import React, { useState, useEffect, useRef } from 'react'
import Header from '../components/landing/Header'
import Footer from '../components/landing/Footer'
import { useLanguage } from '../hooks/useLanguage'
import { contactApi } from '../services/api'

const ContactPage: React.FC = () => {
  const { t } = useLanguage()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const recaptchaWidgetId = useRef<number | null>(null)
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaError, setCaptchaError] = useState<string | null>(null)
  const MIN_MESSAGE_LEN = 10
  const MAX_MESSAGE_LEN = 500

  // Field-level errors
  const [nameError, setNameError] = useState<string>('')
  const [emailError, setEmailError] = useState<string>('')
  const [subjectError, setSubjectError] = useState<string>('')
  const [messageError, setMessageError] = useState<string>('')

  const validateEmail = (email: string) => {
    if (!email) return t('auth.emailRequired')
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) return t('auth.emailInvalid')
    return ''
  }

  const validateName = (name: string) => {
    if (!name) return t('auth.fieldRequired')
    const nameRegex = /^[a-zA-ZÀ-ÿ\s\-']+$/
    if (!nameRegex.test(name)) return t('contact.nameInvalidChars')
    if (name.trim().length < 2) return 'Minimum length is 2 characters.'
    return ''
  }

  const validateSubject = (subject: string) => {
    if (!subject) return t('auth.fieldRequired')
    const subjectRegex = /^[a-zA-Z0-9À-ÿ\s\-'.,!?()]+$/
    if (!subjectRegex.test(subject)) return t('contact.subjectInvalidChars')
    if (subject.trim().length < 3) return 'Minimum length is 3 characters.'
    return ''
  }

  const validateFields = () => {
    const ne = validateName(formData.name)
    const ee = validateEmail(formData.email)
    const se = validateSubject(formData.subject)
    const msgLen = formData.message.trim().length
    const me = msgLen === 0 ? t('auth.fieldRequired') : 
               msgLen < MIN_MESSAGE_LEN ? `Please enter at least ${MIN_MESSAGE_LEN} characters.` :
               msgLen > MAX_MESSAGE_LEN ? `Please enter no more than ${MAX_MESSAGE_LEN} characters.` : ''
    
    setNameError(ne)
    setEmailError(ee)
    setSubjectError(se)
    setMessageError(me)
    return !(ne || ee || se || me)
  }

  // Load Google reCAPTCHA Enterprise script in head
  useEffect(() => {
    const siteKey = import.meta.env.RECAPTCHA_SITE_KEY
    console.log('🔑 reCAPTCHA Site Key loaded:', siteKey ? 'YES' : 'NO')
    if (!siteKey) {
      console.error('❌ RECAPTCHA_SITE_KEY not found in environment variables')
      return
    }

    // Remove any previously rendered reCAPTCHA widgets (from HMR/old code)
    const oldWidgets = document.querySelectorAll('.g-recaptcha')
    oldWidgets.forEach((node) => {
      try { node.parentElement?.removeChild(node) } catch {}
    })

    const existingScript = document.getElementById('recaptcha-script') as HTMLScriptElement | null
    if (!existingScript) {
      console.log('📜 Loading reCAPTCHA Enterprise script in head...')
      const script = document.createElement('script')
      script.id = 'recaptcha-script'
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`
      script.async = true
      script.defer = true
      script.onload = () => {
        console.log('✅ reCAPTCHA Enterprise script loaded successfully')
        // Wait a bit for the script to fully initialize
        setTimeout(() => {
          setRecaptchaReady(true)
        }, 100)
      }
      script.onerror = () => {
        console.error('❌ Failed to load reCAPTCHA Enterprise script')
      }
      // Add to head instead of body
      document.head.appendChild(script)
    } else {
      console.log('📜 reCAPTCHA script already exists')
      setRecaptchaReady(true)
    }
  }, [])

  // Initialize reCAPTCHA v3 when script is ready
  useEffect(() => {
    const siteKey = import.meta.env.RECAPTCHA_SITE_KEY
    if (!recaptchaReady || !siteKey) return
    
    // Wait for grecaptcha to be available
    const checkGrecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        console.log('🚀 reCAPTCHA Enterprise ready, grecaptcha available:', !!window.grecaptcha)
        console.log('🔧 grecaptcha methods:', Object.keys(window.grecaptcha))
        console.log('🔧 grecaptcha.enterprise available:', !!window.grecaptcha?.enterprise)
      } else {
        console.log('⏳ Waiting for grecaptcha to load...')
        setTimeout(checkGrecaptcha, 100)
      }
    }
    
    checkGrecaptcha()
  }, [recaptchaReady])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // live validation
    if (name === 'name') {
      setNameError(validateName(value))
    }
    if (name === 'email') {
      setEmailError(validateEmail(value))
    }
    if (name === 'subject') {
      setSubjectError(validateSubject(value))
    }
    if (name === 'message') {
      const len = value.trim().length
      setMessageError(len === 0 ? t('auth.fieldRequired') : 
                     len < MIN_MESSAGE_LEN ? `Please enter at least ${MIN_MESSAGE_LEN} characters.` :
                     len > MAX_MESSAGE_LEN ? `Please enter no more than ${MAX_MESSAGE_LEN} characters.` : '')
    }
  }

  const performSubmit = async (token: string) => {
    console.log('📤 Submitting contact form with token:', token ? 'YES' : 'NO')
    try {
      const response = await contactApi.sendMessage({ ...formData, recaptchaToken: token } as any)
      console.log('✅ Contact form submitted successfully:', response)
      setSubmitStatus('success')
      setFormData({ name: '', email: '', subject: '', message: '' })
      setCaptchaToken('')
      setCaptchaError(null)
    } catch (error) {
      console.error('❌ Contact submit error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('🎯 Form submit triggered')
    setIsSubmitting(true)
    setSubmitStatus('idle')

    // Validate fields first
    if (!validateFields()) {
      console.log('❌ Form validation failed')
      setIsSubmitting(false)
      return
    }

    console.log('✅ Form validation passed')

    // Execute reCAPTCHA v3 on submit
    const siteKey = import.meta.env.RECAPTCHA_SITE_KEY
    console.log('🔄 Executing reCAPTCHA v3 with site key:', siteKey ? 'YES' : 'NO')
    
    if (!siteKey) {
      console.error('❌ RECAPTCHA_SITE_KEY not found')
      setCaptchaError('Captcha configuration error. Please try again later.')
      setIsSubmitting(false)
      return
    }

    if (!window.grecaptcha || !window.grecaptcha.enterprise) {
      console.error('❌ grecaptcha not available')
      setCaptchaError('Captcha not loaded. Please refresh the page and try again.')
      setIsSubmitting(false)
      return
    }

    try {
      setCaptchaError(null)
      
      // Safety timeout: if Google never calls our callback, fail gracefully
      const guard = setTimeout(() => {
        if (isSubmitting) {
          console.log('⏰ reCAPTCHA timeout after 10 seconds')
          setIsSubmitting(false)
          setCaptchaError('Captcha timeout. Please try again.')
        }
      }, 10000)

      // Execute reCAPTCHA Enterprise v3 (following Google docs exactly)
      window.grecaptcha.enterprise.ready(async () => {
        console.log('🎯 reCAPTCHA Enterprise ready callback triggered')
        try {
          const token = await window.grecaptcha.enterprise.execute(siteKey, { action: 'CONTACT' })
          console.log('🎫 reCAPTCHA Enterprise token received:', token ? 'YES' : 'NO')
          clearTimeout(guard)
          if (token) {
            performSubmit(token)
          } else {
            console.error('❌ reCAPTCHA returned empty token')
            setIsSubmitting(false)
            setCaptchaError('Captcha failed. Please try again.')
          }
        } catch (error) {
          console.error('❌ reCAPTCHA execute error:', error)
          clearTimeout(guard)
          setIsSubmitting(false)
          setCaptchaError('Captcha failed to execute. Please try again.')
        }
      })
    } catch (err) {
      console.error('❌ reCAPTCHA setup error:', err)
      setCaptchaError('Captcha error. Please try again later.')
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">{t('contact.title')}</h1>
          <p className="text-xl">{t('contact.subtitle')}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Company Information */}
          <div className="bg-white rounded-lg shadow-sm p-8 h-full">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('contact.companyInformation')}</h2>
            <div className="flex items-center mb-6">
              <img 
                width="60" 
                height="60" 
                src="/favicon.ico" 
                className="mr-4" 
                alt="La Dolce Vita" 
              />
              <div>
                <h3 className="text-2xl font-bold font-serif italic text-gray-900">La Dolce Vita</h3>
                <p className="text-gray-600">{t('contact.authenticExperiences')}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{t('contact.address')}</p>
                  <p className="text-gray-600">Via Roma 123, 65000 Pescara, Italia</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{t('contact.phone')}</p>
                  <p className="text-gray-600">+39 085 123 4567</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{t('contact.email')}</p>
                  <p className="text-gray-600">
                    <a href="mailto:info@ladolcevitareis.nl" className="underline hover:text-gray-800">info@ladolcevitareis.nl</a>
                  </p>
                </div>
              </div>
 
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{t('contact.vatNumber')}</p>
                  <p className="text-gray-600">12345678901</p>
                </div>
              </div>

              <div>
                <p className="font-semibold text-gray-900 mt-4">{t('landing.footer.followUs')}</p>
                <div className="flex space-x-4 mt-2">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61580521581922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-blue-600 inline-flex items-center"
                    aria-label="Facebook"
                  >
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M22 12.06C22 6.48 17.52 2 11.94 2 6.48 2 2 6.48 2 12.06 2 17.08 5.66 21.21 10.44 22v-7.02H7.9v-2.92h2.54V9.41c0-2.5 1.49-3.88 3.78-3.88 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.89h2.77l-.44 2.92h-2.33V22C18.34 21.21 22 17.08 22 12.06z"/>
                    </svg>
                    <span className="ml-2">Facebook</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/ladolcevitareis"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-pink-600 inline-flex items-center"
                    aria-label="Instagram"
                  >
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3.5A5.5 5.5 0 1112 18.5 5.5 5.5 0 0112 7.5zm0 2A3.5 3.5 0 1015.5 13 3.5 3.5 0 0012 9.5zM18 6.25a1.25 1.25 0 11-1.25 1.25A1.25 1.25 0 0118 6.25z"/>
                    </svg>
                    <span className="ml-2">Instagram</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-sm p-8 h-full">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('contact.sendRequest')}</h2>
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">{t('contact.successMessage')}</p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">{t('contact.errorMessage')}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.nameLabel')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('contact.namePlaceholder')}
                />
                {nameError && <p className="text-red-600 text-sm mt-1">{nameError}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.emailLabel')}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      emailError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('contact.emailPlaceholder')}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                </div>
                {emailError && <p className="text-red-600 text-sm mt-1">{emailError}</p>}
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.subjectLabel')}
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('contact.subjectPlaceholder')}
                />
                {subjectError && <p className="text-red-600 text-sm mt-1">{subjectError}</p>}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.messageLabel')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder={t('contact.messagePlaceholder')}
                />
                <div className="flex justify-between items-center mt-1">
                  {messageError && <p className="text-red-600 text-sm">{messageError}</p>}
                  <p className={`text-sm ml-auto ${
                    formData.message.length > MAX_MESSAGE_LEN 
                      ? 'text-red-600' 
                      : formData.message.length > MAX_MESSAGE_LEN * 0.8 
                        ? 'text-yellow-600' 
                        : 'text-gray-500'
                  }`}>
                    {formData.message.length}/{MAX_MESSAGE_LEN} {t('contact.charactersRemaining')}
                  </p>
                </div>
              </div>

              {/* reCAPTCHA v3 - No visible widget needed */}
              <div style={{ display: 'none' }}>
                {/* reCAPTCHA v3 doesn't need a visible container */}
              </div>
              {captchaError && (
                <p className="text-red-600 text-sm text-center">{captchaError}</p>
              )}

              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !!nameError || !!emailError || !!subjectError || !!messageError
                }
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('contact.submitting') : t('contact.submitButton')}
              </button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default ContactPage 